import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '@mui/material';
import { Stack } from '@mui/system';
import {
    GridCallbackDetails,
    GridColDef,
    GridPaginationModel,
    GridRowParams,
} from '@mui/x-data-grid';

import { DataGrid, DataGridCard } from '@barboxx/admin-components';
import { useAuth, usePaging, useSearchString } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Business } from '@barboxx/models';
import { UserTypes } from '@barboxx/types';

const COLUMNS: GridColDef[] = [
    { field: 'id', headerName: 'Id', width: 30 },
    { field: 'firstName', headerName: 'First Name', width: 170, flex: 1 },
    { field: 'lastName', headerName: 'Last Name', width: 170, flex: 1 },
    {
        field: 'email',
        headerName: 'Email',
        width: 170,
        flex: 1,
    },
    {
        field: 'roles',
        headerName: 'Roles',
        headerAlign: 'right',
        width: 200,
        align: 'right',
        sortable: false,
    },
];

interface Props {
    searchString?: string;
    business?: Business;
}

const userTypes: UserTypes[] = ['Business'];

export function UserTable({ business }: Props) {
    const { id } = useParams<{ id: string }>();
    const businesses = [Number(id)];
    const { InputComponent, searchString } = useSearchString();

    const navigate = useNavigate();
    const {
        auth: { user },
    } = useAuth();
    const { page, size, sort, updatePageModel, updateSort } = usePaging();

    const { data, isLoading, error } = useQuery({
        queryKey: [
            'users',
            userTypes,
            businesses,
            page,
            size,
            sort,
            searchString,
        ],
        queryFn: () =>
            Api.Users.getUsers({
                page,
                size,
                sort,
                searchString,
                userTypes,
                businesses,
            }),
    });

    const handleChangePageModel = (
        model: GridPaginationModel,
        details: GridCallbackDetails
    ) => {
        updatePageModel(model);
    };

    const handleRowClick = (param: GridRowParams) => {
        navigate(`/users/${param.id}`);
    };

    const HeaderComponent = React.useCallback(() => {
        return (
            <Stack direction="row" gap={2}>
                <InputComponent />
                <Button
                    variant="contained"
                    href={`/users/create?businessId=${id}`}
                >
                    Add
                </Button>
            </Stack>
        );
    }, [InputComponent, id]);

    return (
        <DataGridCard title="Employees" HeaderComponent={HeaderComponent}>
            <DataGrid
                columns={COLUMNS.filter(
                    c =>
                        !(
                            c.field === 'userType' &&
                            user.userType === 'Business'
                        )
                )}
                disableColumnMenu
                disableRowSelectionOnClick
                error={error}
                loading={isLoading}
                onRowClick={handleRowClick}
                paginationModel={{
                    page,
                    pageSize: size,
                }}
                onPaginationModelChange={handleChangePageModel}
                onSortModelChange={updateSort}
                rows={data?.data?.results ?? []}
                rowCount={data?.data?.total ?? 0}
            />
        </DataGridCard>
    );
}
